import React, { useContext } from "react";
import { DatePicker, DatePickerInput, Form, Modal, TextArea } from "carbon-components-react";
import { Formik } from "formik";

import { ServicingPayment } from "@eave/shared/types/api";

import DollarInput from "web_modules/shared/carbon/DollarInput";

import { AmortizationContext } from "../AmortizationProvider";
import { dateToShortForm } from "../helpers";

import { apiToPickerDate, pickerToApiDate } from "./helpers";
import { getInitialValues, validationSchema } from "./validation";

const styles = require("./styles.module.scss");

interface Props {
  period?: string | undefined;
  paymentId?: number | undefined;
  onClose(): void;
}

const AddOrEditPaymentModal = ({ period, paymentId, onClose }: Props) => {
  const { busy, payments, createPayment, updatePayment } = useContext(AmortizationContext);

  const payment = payments.find(p => p.id === paymentId);

  const headingAction = payment ? "Editing" : "Adding";
  const headingDate = dateToShortForm(payment ? payment.payment_period : period);

  return (
    <Formik
      validationSchema={validationSchema}
      initialValues={getInitialValues(payment, period)}
      onSubmit={values => {
        if (payment) {
          updatePayment({
            ...payment,
            ...values,
          }).then(onClose);
          return;
        }
        createPayment({
          payment_period: period,
          ...values,
        } as ServicingPayment).then(onClose);
      }}
    >
      {({ errors, handleBlur, handleChange, handleSubmit, setValues, values }) => (
        <Modal
          open
          size="sm"
          modalHeading={`${headingAction} payment for period starting ${headingDate}`}
          primaryButtonText={`${payment ? "Update" : "Save"} payment`}
          secondaryButtonText="Cancel"
          primaryButtonDisabled={busy || !!Object.keys(errors).length}
          onRequestSubmit={handleSubmit}
          onRequestClose={onClose}
        >
          <Form>
            <div className={styles.field}>
              <DatePicker
                id="check-date"
                name="check_date"
                value={values.check_date ? apiToPickerDate(values.check_date) : null}
                disabled={busy}
                onChange={e => {
                  setValues({
                    ...values,
                    check_date: e.length > 0 ? pickerToApiDate(e[0]) : null,
                  });
                }}
                onBlur={handleBlur}
                datePickerType="single"
                dateFormat="m/d/Y"
              >
                <DatePickerInput
                  id="check-date-input"
                  name="check_date"
                  disabled={busy}
                  labelText="Check date"
                  placeholder="mm-dd-yyyy"
                  type="text"
                  size="sm"
                />
              </DatePicker>
            </div>
            <div className={styles.field}>
              <DollarInput
                id="check-amount"
                name="check_amount"
                value={values.check_amount}
                labelText="Check amount"
                invalid={!!errors.check_amount}
                invalidText={errors.check_amount}
                disabled={busy}
                onChange={handleChange}
                onBlur={handleBlur}
              />
            </div>
            <div className={styles.field}>
              <DollarInput
                id="coupon-additional-principal"
                name="coupon_additional_principal"
                value={values.coupon_additional_principal}
                labelText="Coupon – Additional principal"
                invalid={!!errors.coupon_additional_principal}
                invalidText={errors.coupon_additional_principal}
                disabled={busy}
                onChange={handleChange}
                onBlur={handleBlur}
              />
            </div>
            <div className={styles.field}>
              <DollarInput
                id="coupon-additional-escrow"
                name="coupon_additional_escrow"
                value={values.coupon_additional_escrow}
                labelText="Coupon – Additional escrow"
                invalid={!!errors.coupon_additional_escrow}
                invalidText={errors.coupon_additional_escrow}
                disabled={busy}
                onChange={handleChange}
                onBlur={handleBlur}
              />
            </div>
            <div className={styles.field}>
              <DollarInput
                id="coupon-late-charge"
                name="coupon_late_charge"
                value={values.coupon_late_charge}
                labelText="Coupon – Late charge"
                invalid={!!errors.coupon_late_charge}
                invalidText={errors.coupon_late_charge}
                disabled={busy}
                onChange={handleChange}
                onBlur={handleBlur}
              />
            </div>
            <div className={styles.field}>
              <DollarInput
                id="coupon-fees"
                name="coupon_fees"
                value={values.coupon_fees}
                labelText="Coupon – Fees"
                invalid={!!errors.coupon_fees}
                invalidText={errors.coupon_fees}
                disabled={busy}
                onChange={handleChange}
                onBlur={handleBlur}
              />
            </div>
            <div className={styles.field}>
              <DollarInput
                id="coupon-mortgage-recoverable-advances"
                name="coupon_mortgage_recoverable_advances"
                value={values.coupon_mortgage_recoverable_advances}
                labelText="Coupon – Mortgage recoverable advances"
                invalid={!!errors.coupon_mortgage_recoverable_advances}
                invalidText={errors.coupon_mortgage_recoverable_advances}
                disabled={busy}
                onChange={handleChange}
                onBlur={handleBlur}
              />
            </div>
            <div className={styles.field}>
              <DollarInput
                id="coupon-total-amount-enclosed"
                name="coupon_total_amount_enclosed"
                value={values.coupon_total_amount_enclosed}
                labelText="Coupon – Total amount enclosed"
                invalid={!!errors.coupon_total_amount_enclosed}
                invalidText={errors.coupon_total_amount_enclosed}
                disabled={busy}
                onChange={handleChange}
                onBlur={handleBlur}
              />
            </div>
            <div className={styles.field}>
              <TextArea
                id="notes"
                name="notes"
                value={values.notes}
                labelText="Notes (optional)"
                invalid={!!errors.notes}
                invalidText={errors.notes}
                disabled={busy}
                onChange={handleChange}
                onBlur={handleBlur}
                rows={4}
              />
            </div>
          </Form>
        </Modal>
      )}
    </Formik>
  );
};

export default AddOrEditPaymentModal;
